import { useCallback, useMemo, useState } from 'react';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GET_MONTHLY_TAM_PRIME, VALIDATE_MONTHLY_PRIME } from '@bloomays-lib/adapter.api-talent';
import { Role } from '@bloomays-lib/types.shared';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns/format';
import { useActiveTams } from '../../hooks/useActiveTams';
import { usePrimesBreadcrumbContext } from '../../hooks/usePrimesBreadcrumbContext';
import { getPrimePendingReferenceDate } from '@bloomays-lib/utils.shared';
import { useAnnualTamPrimesStore, useAuthStore } from '../../stores';
import { errorLogger } from '../../helpers/error';
import { notify } from '../../helpers/toastify';
import MonthlyPrimeHeader from '../molecules/MonthlyPrimeHeader';
import PrimesPeriodToggle from '../molecules/PrimesPeriodToggle';
import TamRegularisationDrawer from '../molecules/TamRegularisationDrawer';
import MonthlyPrimeTable from '../molecules/MonthlyPrimeTable';
import {
  GetMonthlyTamPrimeQueryData,
  MonthlyTamPrimeQueryResult,
  TamPrimeLine,
  isValidatedMonthlyTamPrime,
} from '../../types/primes';
import {
  canValidateMonthlyPrime,
  deriveHeaderAmounts,
  getTamDisplayName,
  hasIneligibleMissionCras,
  hasPrimeLineErrors,
  mapAggregateToValidationInput,
} from '../../utils/monthlyPrime.utils';
import { buildCLevelAnnualDetailPath } from '../../utils/annualPrime.utils';
import { buildAnnualTamPrimesCacheKey, buildLifetimeMarginCacheKey } from '../../utils/annualTamPrimesCache.utils';

export type MonthlyPrimeProps = {
  tamEmail?: string;
  referenceDate?: Date;
  preloadedMonthlyTamPrime?: MonthlyTamPrimeQueryResult;
};

const MonthlyPrime = ({
  tamEmail: tamEmailOverride,
  referenceDate: referenceDateOverride,
  preloadedMonthlyTamPrime,
}: MonthlyPrimeProps) => {
  const { t } = useTranslation(['primes', 'random']);
  const navigate = useNavigate();
  const auth = useAuthStore((state) => state.auth);
  const { getTamDisplayName: getActiveTamDisplayName, getTamByEmail } = useActiveTams();
  const authTamEmail = typeof auth?.user?.email === 'string' ? auth.user.email.trim() : '';
  const isSystem = auth?.user?.roles?.includes(Role.System) || false;
  const isTamPreview = Boolean(tamEmailOverride);
  const tamEmail = isTamPreview ? tamEmailOverride?.trim() : authTamEmail;
  const defaultReferenceDate = useMemo(() => getPrimePendingReferenceDate(), []);
  const referenceDate = referenceDateOverride ?? defaultReferenceDate;
  const tamDisplayName = isTamPreview ? getActiveTamDisplayName(tamEmail ?? '') : getTamDisplayName(auth?.user);
  const tamSeniority = tamEmail ? getTamByEmail(tamEmail)?.seniority : undefined;
  const tamAvatarUrl = tamEmail
    ? getTamByEmail(tamEmail)?.avatarUrl ?? (isTamPreview ? null : auth?.user?.picture) ?? null
    : null;
  const breadcrumbContext = useMemo(
    () => ({
      tamDisplayName: isTamPreview ? tamDisplayName : undefined,
      tamEmail: isTamPreview ? tamEmail : undefined,
      selectedYear: format(referenceDate, 'yyyy'),
      referenceDate,
    }),
    [isTamPreview, tamDisplayName, tamEmail, referenceDate],
  );
  usePrimesBreadcrumbContext(breadcrumbContext);
  const [regularisationDrawerOpen, setRegularisationDrawerOpen] = useState(false);

  const { loading, error, data, refetch, networkStatus } = useQuery<GetMonthlyTamPrimeQueryData>(
    GET_MONTHLY_TAM_PRIME,
    {
      skip: !tamEmail,
      variables: {
        tamEmail,
        referenceDate,
      },
      context: { clientName: 'api.talents' },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );

  const freshMonthlyAggregate = data?.getMonthlyTamPrime;

  const monthlyAggregate = useMemo(
    () => freshMonthlyAggregate ?? preloadedMonthlyTamPrime,
    [freshMonthlyAggregate, preloadedMonthlyTamPrime],
  );

  const isMonthlyLoading = useMemo(
    () => loading && preloadedMonthlyTamPrime === undefined,
    [loading, preloadedMonthlyTamPrime],
  );

  const isRefetching = networkStatus === NetworkStatus.refetch;

  const isAwaitingFreshMonthlyPrime =
    preloadedMonthlyTamPrime !== undefined && freshMonthlyAggregate === undefined && loading;

  const isHeaderLoading = isMonthlyLoading || isRefetching || isAwaitingFreshMonthlyPrime;

  const headerAmountsSource = isHeaderLoading ? undefined : freshMonthlyAggregate ?? preloadedMonthlyTamPrime;

  const isValidatedMonthly = useMemo(
    () => monthlyAggregate !== undefined && isValidatedMonthlyTamPrime(monthlyAggregate),
    [monthlyAggregate],
  );

  const canManageRegularisation = isSystem && monthlyAggregate !== undefined && !isHeaderLoading && !isValidatedMonthly;

  const hasExistingRegularisation = useMemo(
    () => (headerAmountsSource?.regularisation ?? 0) !== 0,
    [headerAmountsSource],
  );

  const handleValidationCompleted = useCallback(() => {
    notify('success', t('validateSuccess'));
    if (tamEmail) {
      const store = useAnnualTamPrimesStore.getState();
      store.invalidateCache(buildAnnualTamPrimesCacheKey(tamEmail, format(referenceDate, 'yyyy')));
      store.invalidateLifetimeCache(buildLifetimeMarginCacheKey(tamEmail));
      store.invalidateLifetimeCumulsBatchCache();
    }
    if (isTamPreview && tamEmail) {
      navigate(buildCLevelAnnualDetailPath(tamEmail, format(referenceDate, 'yyyy')));
      return;
    }
    navigate('/primes/annuel');
  }, [isTamPreview, navigate, referenceDate, t, tamEmail]);

  const [validateMonthlyPrime, { loading: isValidating }] = useMutation(VALIDATE_MONTHLY_PRIME, {
    context: { clientName: 'api.talents' },
    onCompleted: handleValidationCompleted,
    onError: (mutationError) => {
      errorLogger(mutationError, { extraInfos: 'validateMonthlyPrime failed on /primes/mois' });
      notify('error', t('validateError'), mutationError);
    },
  });

  const handleRegularisationSaved = useCallback(() => {
    void refetch().catch((refreshError) => {
      errorLogger(refreshError, {
        extraInfos: 'getMonthlyTamPrime refresh failed after regularisation save',
      });
      notify('error', t('loadError'), refreshError);
    });
  }, [refetch, t]);

  const handleValidate = useCallback(() => {
    if (!monthlyAggregate || !canValidateMonthlyPrime(monthlyAggregate)) {
      return;
    }
    void validateMonthlyPrime({
      variables: {
        tamEmail,
        referenceDate,
        prime: mapAggregateToValidationInput(monthlyAggregate),
      },
    });
  }, [monthlyAggregate, referenceDate, tamEmail, validateMonthlyPrime]);

  if (!tamEmail) {
    return <ErrorMessage variant="body1">{t('missingTamEmail')}</ErrorMessage>;
  }

  if (error) {
    errorLogger(error, { extraInfos: 'getMonthlyTamPrime failed on /primes' });
    return <ErrorMessage variant="body1">{t('loadError')}</ErrorMessage>;
  }

  const primes: TamPrimeLine[] = isMonthlyLoading ? [] : monthlyAggregate?.primes ?? [];
  const amounts = headerAmountsSource ? deriveHeaderAmounts(headerAmountsSource) : undefined;
  const canValidate = !isMonthlyLoading && monthlyAggregate ? canValidateMonthlyPrime(monthlyAggregate) : false;
  const validationDate =
    isValidatedMonthly && monthlyAggregate !== undefined && isValidatedMonthlyTamPrime(monthlyAggregate)
      ? monthlyAggregate.validationDate
      : undefined;
  const validateDisabledTooltip =
    !isMonthlyLoading && !isValidating && monthlyAggregate !== undefined && !canValidate
      ? hasPrimeLineErrors(monthlyAggregate)
        ? t('validateDisabledErrorLineTooltip')
        : hasIneligibleMissionCras(monthlyAggregate)
        ? t('validateDisabledCraTooltip')
        : undefined
      : undefined;

  return (
    <Container>
      <HeaderSection>
        <ToolbarRow>
          <PrimesPeriodToggle activeView="monthly" tamEmail={tamEmail} referenceDate={referenceDate} />
        </ToolbarRow>
        <MonthlyPrimeHeader
          tamDisplayName={tamDisplayName}
          tamEmail={tamEmail ?? ''}
          tamSeniority={tamSeniority ?? undefined}
          tamAvatarUrl={tamAvatarUrl}
          amounts={amounts}
          loading={isHeaderLoading}
          isValidated={isValidatedMonthly}
          validationDate={validationDate}
          canValidate={canValidate}
          isValidating={isValidating}
          validateDisabledTooltip={validateDisabledTooltip}
          onValidate={handleValidate}
          canAddRegularisation={canManageRegularisation}
          hasExistingRegularisation={hasExistingRegularisation}
          onAddRegularisation={() => setRegularisationDrawerOpen(true)}
        />
      </HeaderSection>
      <TamRegularisationDrawer
        open={regularisationDrawerOpen}
        onClose={() => setRegularisationDrawerOpen(false)}
        onSaved={handleRegularisationSaved}
        tamEmail={tamEmail}
        tamDisplayName={tamDisplayName}
        referenceDate={referenceDate}
      />
      <MonthlyPrimeTable
        rows={primes}
        loading={isMonthlyLoading}
        referenceDate={referenceDate}
        showOnlyMargin={tamSeniority === 'Director'}
      />
    </Container>
  );
};

export default MonthlyPrime;

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const HeaderSection = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(3),
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

const ToolbarRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  color: theme.palette.error.main,
}));
